/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { api } from "@app/data/api";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "../Stock/types";
import { WASTE_REASONS, type Dish, type WasteEntry } from "./types";
import { LogWasteForm, type WasteInput } from "./LogWasteForm";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

const PERIODS = [7, 30, 90] as const;

// What was thrown away, broken, eaten by staff or given away — and what it
// cost. See docs/plans/inventory_and_costing/06_capture.md (#9).
export const InventoryWastePage = defineComponent({
  name: "InventoryWastePage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const saving = ref(false);
    const days = ref<(typeof PERIODS)[number]>(7);
    const entries = ref<WasteEntry[]>([]);
    const stockItems = ref<StockItem[]>([]);
    const dishes = ref<Dish[]>([]);

    const loadEntries = async () => {
      const from = new Date(Date.now() - days.value * 86_400_000).toISOString();
      entries.value = (await trpc.stock.wasteEntries.query({
        from,
      })) as WasteEntry[];
    };

    onMounted(async () => {
      try {
        const [items, costs, products] = await Promise.all([
          trpc.stock.list.query(),
          trpc.recipes.costs.query(),
          api.getProducts(),
        ]);
        stockItems.value = (items as StockItem[]).filter((s) => s.active);
        const withRecipe = new Set(
          costs
            .filter((c) => c.entity_type === "product")
            .map((c) => c.entity_id),
        );
        dishes.value = (products as { id: number; name: string }[])
          .filter((p) => withRecipe.has(p.id))
          .map((p) => ({ id: p.id, name: p.name }));
        await loadEntries();
      } catch {
        toast.showToast(t("inventory.waste.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    watch(days, loadEntries);

    const totals = computed(() => {
      const byReason = new Map<string, number>();
      for (const e of entries.value)
        byReason.set(e.reason, (byReason.get(e.reason) ?? 0) + e.cost_total);
      return WASTE_REASONS.filter((r) => byReason.has(r)).map((r) => ({
        reason: r,
        total: byReason.get(r)!,
      }));
    });
    const grandTotal = computed(() =>
      entries.value.reduce((sum, e) => sum + e.cost_total, 0),
    );

    const log = async (entry: WasteInput) => {
      saving.value = true;
      try {
        await trpc.stock.logWaste.mutate(entry);
        await loadEntries();
        toast.showToast(t("inventory.waste.logged"), { type: "success" });
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ??
            t("inventory.waste.log_failed"),
          {
            type: "error",
          },
        );
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.waste")}
          subtitle={t("inventory.waste.subtitle")}
        >
          <div class="join">
            {PERIODS.map((d) => (
              <button
                key={d}
                class={[
                  "btn btn-sm join-item",
                  days.value === d ? "btn-primary" : "btn-ghost",
                ].join(" ")}
                onClick={() => (days.value = d)}
              >
                {t("inventory.waste.last_days").replace("{days}", String(d))}
              </button>
            ))}
          </div>
        </SubpageHeader>

        {loading.value ? (
          <div class="flex flex-1 items-center justify-center">
            <Loading />
          </div>
        ) : (
          <div class="flex-1 space-y-3 overflow-auto p-3">
            {stockItems.value.length === 0 ? (
              <p class="p-3 text-sm text-base-content/60">
                {t("inventory.recipe.no_stock_items")}
              </p>
            ) : (
              <LogWasteForm
                stockItems={stockItems.value}
                dishes={dishes.value}
                saving={saving.value}
                onSubmit={log}
              />
            )}

            <div class="flex flex-wrap gap-2">
              <div class="rounded-lg border border-base-300 bg-base-100 px-4 py-2">
                <div class="text-xs text-base-content/60">
                  {t("inventory.waste.total")}
                </div>
                <div class="text-lg font-semibold">
                  {grandTotal.value.toFixed(2)}
                </div>
              </div>
              {totals.value.map((x) => (
                <div
                  key={x.reason}
                  class="rounded-lg border border-base-300 bg-base-100 px-4 py-2"
                >
                  <div class="text-xs text-base-content/60">
                    {t(`inventory.waste.reason_${x.reason}`)}
                  </div>
                  <div class="text-lg font-semibold">{x.total.toFixed(2)}</div>
                </div>
              ))}
            </div>

            {entries.value.length === 0 ? (
              <p class="p-3 text-sm text-base-content/60">
                {t("inventory.waste.empty")}
              </p>
            ) : (
              <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>{t("inventory.waste.when")}</th>
                      <th>{t("inventory.waste.what")}</th>
                      <th class="text-right">
                        {t("inventory.recipe.col_qty")}
                      </th>
                      <th>{t("inventory.waste.reason")}</th>
                      <th>{t("inventory.purchases.notes")}</th>
                      <th class="text-right">{t("inventory.waste.cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.value.map((e) => (
                      <tr key={e.id}>
                        <td class="whitespace-nowrap">
                          {new Date(e.occurred_at).toLocaleString()}
                        </td>
                        <td>
                          {e.name}
                          {e.product_id != null && (
                            <span class="badge badge-ghost badge-sm ml-2">
                              {t("inventory.waste.kind_dish")}
                            </span>
                          )}
                        </td>
                        <td class="text-right">
                          {`${e.quantity} ${e.unit ?? t("inventory.waste.portions").toLowerCase()}`}
                        </td>
                        <td>{t(`inventory.waste.reason_${e.reason}`)}</td>
                        <td class="text-base-content/70">{e.note ?? ""}</td>
                        <td class="text-right font-medium">
                          {e.cost_total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
});
